import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useStore } from '../context/StoreContext';
import reportsService from '../services/reportsService';
import invoiceService from '../services/invoiceService';
import purchaseOrderService from '../services/purchaseOrderService';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import useStoreTimezone from '../hooks/useStoreTimezone';
import { formatInStoreTimezone } from '../utils/dateUtils';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const dedupeById = (rows = []) => {
  const map = new Map();
  rows.forEach((row) => {
    if (row?._id && !map.has(row._id)) {
      map.set(row._id, row);
    }
  });
  return Array.from(map.values());
};

export const formatCurrency = (value = 0) =>
  `$${Number(value || 0).toLocaleString('en-AU', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const periodLabels = {
  today: 'Today',
  week: 'This Week',
  month: 'This Month',
  last30days: 'Last 30 Days',
};

const DashboardPage = () => {
  const { user } = useAuth();
  const { activeStore, userStores } = useStore();
  const timezone = useStoreTimezone();

  const [summary, setSummary] = useState(null);
  const [salesChartData, setSalesChartData] = useState(null);
  const [topProducts, setTopProducts] = useState([]);
  const [inventorySnapshot, setInventorySnapshot] = useState(null);
  const [inventorySnapshotDate, setInventorySnapshotDate] = useState(null);
  const [scheduledInvoices, setScheduledInvoices] = useState([]);
  const [pendingPurchaseOrders, setPendingPurchaseOrders] = useState([]);
  const [loadingLists, setLoadingLists] = useState(false);
  const [invoiceSort, setInvoiceSort] = useState({ key: null, direction: 'asc' });
  const [purchaseOrderSort, setPurchaseOrderSort] = useState({ key: null, direction: 'asc' });
  const [error, setError] = useState('');
  const [period, setPeriod] = useState('today');
  const [topProductsRanking, setTopProductsRanking] = useState('revenue');
  const [dashboardStoreId, setDashboardStoreId] = useState(null);
  const [canViewAllStores, setCanViewAllStores] = useState(false);
  const [allScopeStoreIds, setAllScopeStoreIds] = useState([]);

  const canViewReports = user?.permissions?.includes('reports:view');
  const canShowAllStoresOption = canViewAllStores && allScopeStoreIds.length > 1;

  useEffect(() => {
    if (!canViewReports || !activeStore?._id) {
      setCanViewAllStores(false);
      setAllScopeStoreIds([]);
      return;
    }

    let isMounted = true;
    const loadScopeAccess = async () => {
      try {
        const response = await reportsService.getDashboardScopeAccess();
        if (!isMounted) return;
        const data = response?.data?.data || {};
        setCanViewAllStores(Boolean(data.canUseConsolidatedDashboard ?? data.hasAllStoresAccess));
        setAllScopeStoreIds(Array.isArray(data.accessibleStoreIds) ? data.accessibleStoreIds : []);
      } catch (err) {
        if (!isMounted) return;
        setCanViewAllStores(false);
        setAllScopeStoreIds([]);
      }
    };

    loadScopeAccess();
    return () => {
      isMounted = false;
    };
  }, [activeStore?._id, canViewReports]);

  useEffect(() => {
    if (!activeStore?._id) return;
    setDashboardStoreId((prev) => {
      if (!prev) return activeStore._id;
      if (prev === 'all') {
        return canViewAllStores ? prev : activeStore._id;
      }
      const storeStillAssigned = userStores.some((store) => store?._id === prev);
      return storeStillAssigned ? prev : activeStore._id;
    });
  }, [activeStore?._id, userStores, canViewAllStores]);

  useEffect(() => {
    if (!canViewReports || !activeStore || !dashboardStoreId) {
      setSummary(null);
      setSalesChartData(null);
      setTopProducts([]);
      setInventorySnapshot(null);
      setInventorySnapshotDate(null);
      setScheduledInvoices([]);
      setPendingPurchaseOrders([]);
      return;
    }

    const isAllScope = dashboardStoreId === 'all' && canViewAllStores;
    const targetStoreIds = isAllScope ? allScopeStoreIds : [dashboardStoreId];
    if (!targetStoreIds.length) {
      setSummary(null);
      setSalesChartData(null);
      setTopProducts([]);
      setInventorySnapshot(null);
      setInventorySnapshotDate(null);
      setScheduledInvoices([]);
      setPendingPurchaseOrders([]);
      return;
    }

    let isMounted = true;
    const fetchData = async () => {
      try {
        setError('');
        setLoadingLists(true);

        const summaryParams = isAllScope
          ? { period, timezone, scope: 'all' }
          : { period, timezone, storeId: dashboardStoreId };
        const salesParams = isAllScope
          ? { period, timezone, scope: 'all' }
          : { period, timezone, storeId: dashboardStoreId };
        const topProductsParams = isAllScope
          ? { period, ranking: topProductsRanking, timezone, limit: 5, scope: 'all' }
          : { period, ranking: topProductsRanking, timezone, limit: 5, storeId: dashboardStoreId };

        const [summaryRes, salesOverTimeRes, topProductsRes] = await Promise.all([
          reportsService.getSalesSummary(summaryParams),
          reportsService.getSalesOverTime(salesParams),
          reportsService.getTopProducts(topProductsParams),
        ]);

        let nextInventorySnapshot = null;
        let nextInventorySnapshotDate = null;
        let nextInvoices = [];
        let nextPurchaseOrders = [];

        if (isAllScope) {
          const [snapshotResponses, scheduledResponses, unpaidResponses, poResponses] = await Promise.all([
            Promise.all(targetStoreIds.map((storeId) => reportsService.getInventorySnapshots({ storeId, limit: 1 }))),
            Promise.all(
              targetStoreIds.map((storeId) =>
                invoiceService.fetchInvoices({
                  storeId,
                  status: 'Scheduled',
                  page: 1,
                  limit: 10,
                })
              )
            ),
            Promise.all(
              targetStoreIds.map((storeId) =>
                invoiceService.fetchInvoices({
                  storeId,
                  status: 'Unpaid',
                  page: 1,
                  limit: 10,
                })
              )
            ),
            Promise.all(
              targetStoreIds.map((storeId) =>
                purchaseOrderService.fetchPurchaseOrders({
                  storeId,
                  status: 'Submitted',
                  page: 1,
                  limit: 10,
                })
              )
            ),
          ]);

          nextInventorySnapshot = snapshotResponses.reduce(
            (acc, response) => {
              const summaryData = response?.data?.summary || {};
              acc.totalSkus += Number(summaryData.totalSkus || 0);
              acc.totalUnits += Number(summaryData.totalUnits || 0);
              acc.inventoryValue += Number(summaryData.inventoryValue || 0);
              acc.retailValue += Number(summaryData.retailValue || 0);
              return acc;
            },
            { totalSkus: 0, totalUnits: 0, inventoryValue: 0, retailValue: 0 }
          );

          const scheduled = scheduledResponses.flatMap((response) => response?.data || []);
          const unpaid = unpaidResponses.flatMap((response) => response?.data || []);
          nextInvoices = dedupeById([...scheduled, ...unpaid]);

          const pendingOrders = poResponses.flatMap((response) => response?.data || []);
          nextPurchaseOrders = dedupeById(pendingOrders);
        } else {
          const [snapshotRes, scheduledRes, unpaidRes, poRes] = await Promise.all([
            reportsService.getInventorySnapshots({ storeId: dashboardStoreId, limit: 1 }),
            invoiceService.fetchInvoices({
              storeId: dashboardStoreId,
              status: 'Scheduled',
              page: 1,
              limit: 10,
            }),
            invoiceService.fetchInvoices({
              storeId: dashboardStoreId,
              status: 'Unpaid',
              page: 1,
              limit: 10,
            }),
            purchaseOrderService.fetchPurchaseOrders({
              storeId: dashboardStoreId,
              status: 'Submitted',
              page: 1,
              limit: 10,
            }),
          ]);

          nextInventorySnapshot = snapshotRes.data.summary || null;
          nextInventorySnapshotDate = snapshotRes.data.snapshotDate || null;
          const scheduled = scheduledRes.data || [];
          const unpaid = unpaidRes.data || [];
          nextInvoices = dedupeById([...scheduled, ...unpaid]);
          nextPurchaseOrders = poRes.data || [];
        }

        if (!isMounted) return;
        setSummary(summaryRes.data.data);
        setTopProducts(topProductsRes.data.data || []);
        setInventorySnapshot(nextInventorySnapshot);
        setInventorySnapshotDate(nextInventorySnapshotDate);
        setScheduledInvoices(nextInvoices);
        setPendingPurchaseOrders(nextPurchaseOrders);

        const labelFormat = 'dd-MM-yyyy';
        const chartRows = salesOverTimeRes?.data?.data || [];
        const chartLabels = chartRows.map((row) =>
          formatInStoreTimezone(row._id, labelFormat, timezone)
        );
        const chartDataPoints = chartRows.map((row) => row.totalSales);
        setSalesChartData({
          labels: chartLabels,
          datasets: [
            {
              label: 'Sales ($)',
              data: chartDataPoints,
              backgroundColor: 'rgba(54, 162, 235, 0.6)',
            },
          ],
        });
      } catch (err) {
        if (!isMounted) return;
        setError(err?.response?.data?.message || 'Could not load dashboard data.');
      } finally {
        if (isMounted) {
          setLoadingLists(false);
        }
      }
    };

    fetchData();
    return () => {
      isMounted = false;
    };
  }, [activeStore, allScopeStoreIds, canViewAllStores, canViewReports, dashboardStoreId, period, timezone, topProductsRanking]);

  const toggleSort = (setter, sortState, key) => {
    setter((prev) => {
      if (prev.key !== key) {
        return { key, direction: 'asc' };
      }
      return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
    });
  };

  const sortRows = (rows, sortState, getValue) => {
    if (!sortState.key) return rows;
    const direction = sortState.direction === 'asc' ? 1 : -1;
    return rows
      .map((row, index) => ({ row, index, value: getValue(row) }))
      .sort((a, b) => {
        if (a.value === undefined || a.value === null || a.value === '') return 1;
        if (b.value === undefined || b.value === null || b.value === '') return -1;
        if (a.value > b.value) return direction;
        if (a.value < b.value) return -direction;
        return a.index - b.index;
      })
      .map((entry) => entry.row);
  };

  const sortedInvoices = useMemo(() => {
    return sortRows(scheduledInvoices, invoiceSort, (invoice) => {
      switch (invoiceSort.key) {
        case 'invoice':
          return String(invoice.invoiceNumber || '').toLowerCase();
        case 'supplier':
          return String(invoice.supplier?.name || '').toLowerCase();
        case 'date':
          return new Date(invoice.scheduledPaymentDate || invoice.dueDate || 0).getTime();
        case 'amount':
          return Number(invoice.totalAmount || 0);
        default:
          return '';
      }
    });
  }, [scheduledInvoices, invoiceSort]);

  const sortedPurchaseOrders = useMemo(() => {
    return sortRows(pendingPurchaseOrders, purchaseOrderSort, (order) => {
      switch (purchaseOrderSort.key) {
        case 'po':
          return String(order.poNumber || '').toLowerCase();
        case 'supplier':
          return String(order.supplier?.name || '').toLowerCase();
        case 'created':
          return new Date(order.createdAt || 0).getTime();
        case 'total':
          return Number(order.totalAmount || order.amount || 0);
        default:
          return '';
      }
    });
  }, [pendingPurchaseOrders, purchaseOrderSort]);

  const selectedStoreName = useMemo(() => {
    if (dashboardStoreId === 'all') {
      return 'All Stores';
    }
    const selected = userStores.find((store) => store?._id === dashboardStoreId);
    return selected?.name || activeStore?.name || 'Store';
  }, [activeStore?.name, dashboardStoreId, userStores]);

  const summaryData = summary || {
    totalSales: 0,
    totalGst: 0,
    transactionCount: 0,
    avgSaleValue: 0,
  };

  const salesChartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        ticks: {
          callback: function callback(value, index) {
            const dateLabel = this.getLabelForValue(value);
            const saleValue = Number(salesChartData?.datasets?.[0]?.data?.[index] || 0);
            return [dateLabel, formatCurrency(saleValue)];
          },
        },
      },
    },
  }), [salesChartData]);

  if (!canViewReports) {
    return (
      <div className="p-6">
        <h1 className="text-3xl font-bold text-gray-800">Welcome, {user.name}!</h1>
        <p className="mt-4 text-gray-600">Select an option from the sidebar to get started.</p>
        <div className="mt-8">
          <Link to="/checkout" className="bg-primary text-white font-bold py-3 px-6 rounded-lg hover:bg-primary-dark transition-colors text-lg">
            Start New Sale
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between mb-6">
        <h1 className="text-3xl font-bold text-gray-800">
          Dashboard: <span className="text-blue-600">{selectedStoreName}</span>
        </h1>
        <div className="w-full md:w-80">
          <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
            Dashboard Store
          </label>
          <select
            value={dashboardStoreId || ''}
            onChange={(event) => setDashboardStoreId(event.target.value)}
            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {userStores.map((store) => (
              <option key={store._id} value={store._id}>
                {store.name}
              </option>
            ))}
            {canShowAllStoresOption && (
              <option value="all">All Stores (Consolidated)</option>
            )}
          </select>
        </div>
      </div>

      {error && <p className="bg-red-100 text-red-700 p-3 rounded mb-4">{error}</p>}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-6 mb-6">
        <div className="bg-white p-4 rounded-lg shadow flex flex-col justify-between min-h-[120px]"><h3 className="text-gray-500">Total Sales</h3><p className="text-3xl font-bold">{formatCurrency(summaryData.totalSales || 0)}</p></div>
        <div className="bg-white p-4 rounded-lg shadow flex flex-col justify-between min-h-[120px]"><h3 className="text-gray-500">Transactions</h3><p className="text-3xl font-bold">{summaryData.transactionCount || 0}</p></div>
        <div className="bg-white p-4 rounded-lg shadow flex flex-col justify-between min-h-[120px]"><h3 className="text-gray-500">Avg. Sale Value</h3><p className="text-3xl font-bold">{formatCurrency(summaryData.avgSaleValue || 0)}</p></div>
        <div className="bg-white p-4 rounded-lg shadow flex flex-col justify-between min-h-[120px]"><h3 className="text-gray-500">GST Collected</h3><p className="text-3xl font-bold">{formatCurrency(summaryData.totalGst || 0)}</p></div>
        <div className="bg-white p-4 rounded-lg shadow flex flex-col justify-between min-h-[120px]">
          <h3 className="text-gray-500">Inventory Value</h3>
          <p className="text-3xl font-bold">{formatCurrency(inventorySnapshot?.inventoryValue || 0)}</p>
          <p className="text-xs text-gray-400 mt-2">
            {dashboardStoreId === 'all'
              ? 'Snapshot across all stores'
              : `Snapshot ${inventorySnapshotDate ? formatInStoreTimezone(new Date(`${inventorySnapshotDate}T00:00:00`), 'dd-MM-yyyy', timezone) : '—'}`}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 space-y-6">
          <div className="bg-white p-4 rounded-lg shadow h-[360px] flex flex-col">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
              <h3 className="font-bold">
                Sales {periodLabels[period] || periodLabels.today}
              </h3>
              <div className="flex flex-wrap gap-2 bg-slate-100 p-1 rounded-lg" aria-label="Revenue period">
                <button type="button" disabled={loadingLists} onClick={() => setPeriod('today')} className={`min-h-11 px-4 py-1 rounded-md text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-60 ${period === 'today' ? 'bg-blue-500 text-white' : 'text-gray-600'}`}>Today</button>
                <button type="button" disabled={loadingLists} onClick={() => setPeriod('week')} className={`min-h-11 px-4 py-1 rounded-md text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-60 ${period === 'week' ? 'bg-blue-500 text-white' : 'text-gray-600'}`}>This Week</button>
                <button type="button" disabled={loadingLists} onClick={() => setPeriod('month')} className={`min-h-11 px-4 py-1 rounded-md text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-60 ${period === 'month' ? 'bg-blue-500 text-white' : 'text-gray-600'}`}>This Month</button>
                <button type="button" disabled={loadingLists} onClick={() => setPeriod('last30days')} className={`min-h-11 px-4 py-1 rounded-md text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-60 ${period === 'last30days' ? 'bg-blue-500 text-white' : 'text-gray-600'}`}>Last 30 Days</button>
              </div>
            </div>
            <div className="flex-1">
              {salesChartData && <Bar options={salesChartOptions} data={salesChartData} />}
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow h-[360px] flex flex-col">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-2">
              <h3 className="font-bold">Top Products</h3>
              <div className="flex rounded-lg bg-slate-100 p-1" aria-label="Top product ranking">
                <button
                  type="button"
                  onClick={() => setTopProductsRanking('revenue')}
                  disabled={loadingLists}
                  className={`min-h-11 px-3 rounded-md text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-60 ${topProductsRanking === 'revenue' ? 'bg-blue-500 text-white' : 'text-gray-600'}`}
                >
                  Revenue
                </button>
                <button
                  type="button"
                  onClick={() => setTopProductsRanking('quantity')}
                  disabled={loadingLists}
                  className={`min-h-11 px-3 rounded-md text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-60 ${topProductsRanking === 'quantity' ? 'bg-blue-500 text-white' : 'text-gray-600'}`}
                >
                  Quantity
                </button>
              </div>
            </div>
            <p className="mb-3 text-xs text-gray-500">
              {periodLabels[period] || periodLabels.today} · Sales with any refund are excluded.
            </p>
            <ul className="space-y-2 flex-1 overflow-y-auto">
              {topProducts.map((product) => (
                <li key={product._id} className="flex justify-between border-b py-2">
                  <span>{product.name}</span>
                  <span className="font-semibold">
                    {topProductsRanking === 'revenue'
                      ? formatCurrency(product.totalRevenue)
                      : `${product.totalQuantity} sold`}
                  </span>
                </li>
              ))}
              {topProducts.length === 0 && (
                <li className="text-sm text-gray-500">No completed, non-refunded product sales in this period.</li>
              )}
            </ul>
          </div>
        </div>
        <div className="space-y-6">
          <div className="bg-white p-4 rounded-lg shadow h-[360px] flex flex-col">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold">Unpaid Invoices</h3>
              {loadingLists && <span className="text-xs text-gray-400">Loading…</span>}
            </div>
            <div className="flex-1 overflow-y-auto border border-gray-100 rounded">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 uppercase text-xs sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left">
                      <button
                        type="button"
                        onClick={() => toggleSort(setInvoiceSort, invoiceSort, 'invoice')}
                        className="font-semibold"
                      >
                        Invoice
                      </button>
                    </th>
                    <th className="px-3 py-2 text-left">
                      <button
                        type="button"
                        onClick={() => toggleSort(setInvoiceSort, invoiceSort, 'supplier')}
                        className="font-semibold"
                      >
                        Supplier
                      </button>
                    </th>
                    <th className="px-3 py-2 text-left">
                      <button
                        type="button"
                        onClick={() => toggleSort(setInvoiceSort, invoiceSort, 'date')}
                        className="font-semibold"
                      >
                        Due / Scheduled
                      </button>
                    </th>
                    <th className="px-3 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => toggleSort(setInvoiceSort, invoiceSort, 'amount')}
                        className="font-semibold"
                      >
                        Amount
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedInvoices.map((invoice) => (
                    <tr key={invoice._id} className="border-b">
                      <td className="px-3 py-2">{invoice.invoiceNumber || '—'}</td>
                      <td className="px-3 py-2">{invoice.supplier?.name || '—'}</td>
                      <td className="px-3 py-2">
                        {formatInStoreTimezone(
                          invoice.scheduledPaymentDate || invoice.dueDate,
                          'dd-MM-yyyy',
                          timezone
                        ) || '—'}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {formatCurrency(invoice.totalAmount || 0)}
                      </td>
                    </tr>
                  ))}
                  {!scheduledInvoices.length && (
                    <tr>
                      <td colSpan="4" className="px-3 py-4 text-center text-gray-500">
                        No scheduled invoices in the next 7 days.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow h-[360px] flex flex-col">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold">Purchase Orders Pending Approval</h3>
              {loadingLists && <span className="text-xs text-gray-400">Loading…</span>}
            </div>
            <div className="flex-1 overflow-y-auto border border-gray-100 rounded">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 uppercase text-xs sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left">
                      <button
                        type="button"
                        onClick={() => toggleSort(setPurchaseOrderSort, purchaseOrderSort, 'po')}
                        className="font-semibold"
                      >
                        PO #
                      </button>
                    </th>
                    <th className="px-3 py-2 text-left">
                      <button
                        type="button"
                        onClick={() => toggleSort(setPurchaseOrderSort, purchaseOrderSort, 'supplier')}
                        className="font-semibold"
                      >
                        Supplier
                      </button>
                    </th>
                    <th className="px-3 py-2 text-left">
                      <button
                        type="button"
                        onClick={() => toggleSort(setPurchaseOrderSort, purchaseOrderSort, 'created')}
                        className="font-semibold"
                      >
                        Created
                      </button>
                    </th>
                    <th className="px-3 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => toggleSort(setPurchaseOrderSort, purchaseOrderSort, 'total')}
                        className="font-semibold"
                      >
                        Total
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedPurchaseOrders.map((order) => (
                    <tr key={order._id} className="border-b">
                      <td className="px-3 py-2">{order.poNumber}</td>
                      <td className="px-3 py-2">{order.supplier?.name || '—'}</td>
                      <td className="px-3 py-2">
                        {order.createdAt ? formatInStoreTimezone(order.createdAt, 'dd-MM-yyyy', timezone) : '—'}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {formatCurrency(order.totalAmount || order.amount || 0)}
                      </td>
                    </tr>
                  ))}
                  {!pendingPurchaseOrders.length && (
                    <tr>
                      <td colSpan="4" className="px-3 py-4 text-center text-gray-500">
                        No pending purchase orders.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
